import { useCallback, useState, useRef } from 'react'
import { InputNumber, message, Radio } from 'antd'
import { useMount } from 'react-use'
import { useMutation } from '@apollo/client'
import classNames from 'classnames'
import styled from 'styled-components'

import { MAX_POINT_RECHARGE, MIN_POINT_RECHARGE } from '~/configs'
import { graphQLErrorMessage, YuanToFen } from '~/utils'
import { useImperativeQuery } from '~/hooks/useImperativeQuery'
import { useDebounce } from '~/hooks/useDebounce'

import { MyLink } from '~/components/ui/MyLink'
import { MyForm } from '~/components/ui/MyForm'
import { MyModal } from '~/components/ui/MyModal'
import { MyBtn } from '~/components/ui/MyBtn'
import { InfoCard, InfoCardItem } from './InfoCard'
import { ScanPayModal } from './ScanPayModal'

import {
  GET_PAY_TYPE_LIST,
  GET_RECHARGE_POINT,
  RECHARGE_BALANCE,
} from '~/graphqls/gqls'
import {
  getPayTypeList,
  getPayTypeListVariables,
  getPayTypeList_settlementModule_payTypeList,
  getRechargePoint,
  getRechargePointVariables,
  PayScene,
  rechargeBalance,
  rechargeBalanceVariables,
  rechargeBalance_orderModule_copyrightRechargeBalance,
  AccountGroupTypeEnum,
} from '~/graphqls/types'

const StyledTips = styled.div`
  margin: 24px 0;
  color: ${props => props.theme['neutral-400']};
  padding-bottom: 24px;
  border-bottom: 1px solid ${props => props.theme['neutral-300']};
`

const StyledTipsContent = styled.div`
  font-weight: 400;
  font-size: 12px;
  line-height: 20px;
  & + & {
    margin-top: 12px;
  }
`

const quickAmount = [
  {
    value: 20,
    point: 211,
    extra: 11,
  },
  {
    value: 100,
    point: 1111,
    extra: 111,
  },
  {
    value: 300,
    point: 3529,
    extra: 529,
  },
]

const defaultIndex = 1
export const RechargeModal: React.FC<{
  visible: boolean
  setVisible: React.Dispatch<React.SetStateAction<boolean>>
}> = ({ visible, setVisible }) => {
  const [loading, setLoading] = useState(false)
  const [currentIndex, setCurrentIndex] = useState<number>(defaultIndex)
  /** 解决积分异步获取问题：只要有高亮的index,则丢掉异步获取的积分值 */
  const currentIndexRef = useRef<number>(defaultIndex)
  const [scanPayVisible, setScanPayVisible] = useState(false)
  const [chargeAmount, setChargeAmount] = useState<number | undefined>(
    quickAmount[defaultIndex].value
  )
  const [errorStr, setErrorStr] = useState('')
  const [point, setPoint] = useState(quickAmount[defaultIndex].point)

  const [payInfo, setPayInfo] =
    useState<rechargeBalance_orderModule_copyrightRechargeBalance | null>(null)
  const [amount, setAmount] = useState(0)

  const [payTypeList, setPayTypeList] = useState<
    getPayTypeList_settlementModule_payTypeList[]
  >([])

  const [currentPayType, setCurrentPayType] =
    useState<getPayTypeList_settlementModule_payTypeList | null>(null)

  const getRechargePoint = useImperativeQuery<
    getRechargePoint,
    getRechargePointVariables
  >(GET_RECHARGE_POINT)
  const getPayTypeList = useImperativeQuery<
    getPayTypeList,
    getPayTypeListVariables
  >(GET_PAY_TYPE_LIST)
  const [rechargeAction] = useMutation<
    rechargeBalance,
    rechargeBalanceVariables
  >(RECHARGE_BALANCE)

  /** 赋值操作 */
  const setCurrentIndexVal = (val: number) => {
    setCurrentIndex(val)
    currentIndexRef.current = val
  }

  const onChangeAmount = (value: number, index: number, point: number) => {
    if (currentIndex === index) return

    setCurrentIndexVal(index)
    setChargeAmount(value)
    setPoint(point)
  }

  const validateAmount = (value: number | undefined) => {
    if (!value || value === 0) {
      setErrorStr('请输入充值金额')
      return false
    }
    if (!/^(\d+)$/.test(value + '')) {
      setErrorStr('充值金额必须为整数')
      return false
    }

    setErrorStr('')
    return true
  }

  const onConfirm = async () => {
    if (!validateAmount(chargeAmount)) {
      return
    }

    setLoading(true)

    rechargeAction({
      variables: {
        param: {
          rechargeAmount: YuanToFen(chargeAmount),
          rechargeType: AccountGroupTypeEnum.POINT_WALLET,
          payTypeInfo: {
            payChannel: currentPayType?.payChannel,
            payType: currentPayType?.type,
            payProvider: currentPayType?.payProvider,
          },
        },
      },
    })
      .then(data => {
        setAmount(YuanToFen(chargeAmount))
        setPayInfo(
          data.data?.orderModule
            ?.copyrightRechargeBalance as rechargeBalance_orderModule_copyrightRechargeBalance
        )

        setScanPayVisible(true)
      })
      .catch(err => {
        message.destroy()
        message.error(graphQLErrorMessage(err) || '充值订单生成失败')
      })
      .finally(() => {
        setLoading(false)
      })
  }

  const debounceSetPoint = useDebounce(
    (value: number) => {
      getRechargePoint({ amount: YuanToFen(value) }).then(res => {
        // 只有非高亮情况才进行异步赋值
        currentIndexRef.current === -1 && setPoint(res.data.point)
      })
    },
    [getRechargePoint, setPoint],
    500,
    { leading: false, trailing: true }
  )

  const handleAmountChange = (value: number) => {
    setChargeAmount(value)
    if (validateAmount(value)) {
      debounceSetPoint(value)
    }

    const index = quickAmount.findIndex(it => it.value === value)

    if (index !== -1) {
      setCurrentIndexVal(index)
      setPoint(quickAmount[index].point)
    } else {
      setCurrentIndexVal(-1)
    }
  }

  const onSelectPayType = useCallback(
    (item: getPayTypeList_settlementModule_payTypeList) => {
      setCurrentPayType(item)
    },
    []
  )

  useMount(() => {
    getPayTypeList({
      payScene: PayScene.ORIGIN,
    }).then(data => {
      setPayTypeList(
        data?.data?.settlementModule
          ?.payTypeList as getPayTypeList_settlementModule_payTypeList[]
      )

      setCurrentPayType(
        data?.data?.settlementModule?.payTypeList?.filter(
          it => !!it?.available
        )?.[0] as getPayTypeList_settlementModule_payTypeList
      )
    })
  })

  return (
    <MyModal
      visible={visible}
      title="充值"
      onCancel={() => {
        setVisible(false)
      }}
      maskStyle={{
        backgroundColor: 'rgba(0,0,0,0.8)',
      }}
      dark={true}
      width={448}
      footer={null}
      zIndex={10000}
    >
      <MyForm layout="vertical" $dark={true}>
        <MyForm.Item
          name="amount"
          label="充值金额"
          validateStatus={errorStr ? 'error' : undefined}
          help={errorStr}
        >
          {/* 包个div，不让form控制inputNumber, 但是使用form的样式 */}
          <div>
            <InputNumber
              min={MIN_POINT_RECHARGE}
              max={MAX_POINT_RECHARGE}
              value={chargeAmount}
              onChange={val => {
                handleAmountChange(val as number)
              }}
              controls={false}
              addonAfter={
                <div className="bg-neutral-200 text-sm px-4 py-1 rounded-lg text-neutral-500 font-semibold flex items-center">
                  <span className="w-4 h-4 inline-block mr-2">
                    <img
                      src="/images/icons/gold.svg"
                      style={{
                        width: 16,
                        height: 16,
                        verticalAlign: 'top',
                        display: 'inline-block',
                      }}
                      alt=""
                    />
                  </span>{' '}
                  <span
                    className={classNames({
                      'text-neutral-800': point !== 0,
                    })}
                  >
                    {point} 积分
                  </span>
                </div>
              }
              placeholder="请输入充值金额"
            />
          </div>
        </MyForm.Item>
        <div className="flex items-center justify-between pt-4 pb-8">
          {quickAmount.map((item, index) => (
            <div
              className={classNames(
                ' w-[122px] flex items-center justify-center flex-col bg-neutral-200 rounded-lg py-4 cursor-pointer select-none',
                {
                  'border-[#F9DEB9] border-[2px] border-solid bg-neutral-100':
                    currentIndex === index,
                }
              )}
              key={index}
              onClick={() => onChangeAmount(item.value, index, item.point)}
            >
              <div className="text-[14px] text-[#F9DEB9] mb-4">
                售价 {item.value}
              </div>
              <div className="flex items-center font-semibold text-neutral-800 mb-4 leading-5">
                <img
                  src="/images/icons/gold.svg"
                  style={{
                    marginRight: 8,
                    width: 16,
                    verticalAlign: 'top',
                  }}
                  alt=""
                />
                <span className="text-[20px] text-[#F9DEB9] font-semibold">
                  {item.point}
                </span>
                <span
                  className="ml-1 text-xs line-through font-normal"
                  style={{ color: 'rgba(249, 222, 185, 0.5)' }}
                >
                  {item.point - item.extra}
                </span>
              </div>
              <div
                className="bg-[#F9DEB9] rounded-full leading-5 text-neutral-100 text-xs font-semibold"
                style={{ padding: '2px 14px' }}
              >
                多送{item.extra}积分
              </div>
            </div>
          ))}
        </div>
        <InfoCard style={{ marginBottom: 24 }}>
          {payTypeList
            .filter(item => item.available)
            .map(item => (
              <InfoCardItem
                key={item.name}
                label={
                  <>
                    <img
                      width={24}
                      style={{ marginRight: 8, marginTop: -3 }}
                      src={item.icon || ''}
                      alt=""
                    />
                    {item.name}
                  </>
                }
              >
                <Radio
                  style={{ marginRight: 0 }}
                  checked={currentPayType?.name === item.name}
                  onChange={() => onSelectPayType(item)}
                />
              </InfoCardItem>
            ))}
        </InfoCard>
        <StyledTips>
          <StyledTipsContent>
            说明：单笔最小充值{MIN_POINT_RECHARGE}元
            <br />
            充值金额大于等于 {MIN_POINT_RECHARGE} 元，小于 20 元时，0.1元/积分；
            <br />
            充值金额大于等于 20 元，小于 100 元时，0.095 元/积分；
            <br />
            充值金额大于等于 100 元，小于 300 元时，0.09元/积分；
            <br />
            充值金额大于等于 300 元时，0.085元/积分。
          </StyledTipsContent>
          <StyledTipsContent>
            点击立即充值表示您已同意
            <MyLink
              href="/us/integrate-recharge"
              exist={true}
              className="primary-color"
            >
              《积分充值协议》
            </MyLink>
          </StyledTipsContent>
        </StyledTips>
        <MyBtn
          onClick={onConfirm}
          type="primary"
          size="large"
          block
          loading={loading}
        >
          立即充值
        </MyBtn>
      </MyForm>
      <ScanPayModal
        visible={scanPayVisible}
        setVisible={setScanPayVisible}
        icon={currentPayType?.icon || ''}
        payInfo={payInfo}
        amount={amount}
      />
    </MyModal>
  )
}
